import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Pause, X, ArrowCounterClockwise, FlipHorizontal } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function Teleprompter() {
  useSeo("Teleprompter", "Read your script hands-free with adjustable scroll speed and text size.");
  const { id } = useParams();
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [fontSize, setFontSize] = useState(44);
  const [mirror, setMirror] = useState(false);
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const speedRef = useRef(speed);
  speedRef.current = speed;

  useEffect(() => {
    api.get("/scripts").then((res) => {
      const s = res.data.find((x) => x.id === id);
      if (!s) return navigate("/calendar");
      setScript(s);
    });
  }, [id, navigate]);

  const step = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop += speedRef.current * 0.65;
    rafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    if (playing) rafRef.current = requestAnimationFrame(step);
    else cancelAnimationFrame(rafRef.current);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
      if (e.code === "Escape") navigate(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (!script) return null;

  const restart = () => {
    scrollRef.current.scrollTop = 0;
    setPlaying(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="teleprompter-overlay">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 sm:px-16 lg:px-32" style={{ transform: mirror ? "scaleX(-1)" : "none" }}>
        <div className="h-[45vh]" />
        <div className="max-w-4xl mx-auto space-y-10 leading-relaxed font-semibold" style={{ fontSize: `${fontSize}px` }} data-testid="teleprompter-text">
          {script.title && <p className="text-[var(--glow)]" style={{ fontSize: `${fontSize * 0.6}px` }}>{script.title}</p>}
          {script.hook && <p className="text-[#FF4D4D]">{script.hook}</p>}
          {(script.mainPoints || []).map((p, i) => (
            <p key={`${i}:${p.slice(0, 20)}`}>
              <span className="text-[var(--glow)] mr-3">{i + 1}.</span>{p}
            </p>
          ))}
        </div>
        <div className="h-[55vh]" />
      </div>

      <div className="absolute top-1/2 left-0 right-0 h-px bg-[#FF4D4D]/50 pointer-events-none" />

      <div className="glass border-t border-white/10 px-4 py-3 flex flex-wrap items-center gap-4 sm:gap-6 justify-center">
        <button data-testid="teleprompter-play-btn" onClick={() => setPlaying(!playing)} className="w-11 h-11 rounded-full bg-[var(--glow)] flex items-center justify-center hover:bg-[#6a4ce6] transition-colors">
          {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
        </button>
        <button data-testid="teleprompter-restart-btn" onClick={restart} title="Restart" className="text-white/60 hover:text-white transition-colors">
          <ArrowCounterClockwise size={20} />
        </button>
        <label className="flex items-center gap-2 text-xs text-white/60">
          Speed
          <input data-testid="teleprompter-speed" type="range" min="0.5" max="6" step="0.25" value={speed} onChange={(e) => setSpeed(+e.target.value)} className="accent-[#7C5CFF] w-24 sm:w-32" />
          <span className="text-white font-bold w-8">{speed.toFixed(1)}x</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-white/60">
          Size
          <input data-testid="teleprompter-fontsize" type="range" min="24" max="80" step="2" value={fontSize} onChange={(e) => setFontSize(+e.target.value)} className="accent-[#7C5CFF] w-24 sm:w-32" />
        </label>
        <button data-testid="teleprompter-mirror-btn" onClick={() => setMirror(!mirror)} title="Mirror (for teleprompter glass)" className={`transition-colors ${mirror ? "text-[var(--glow)]" : "text-white/60 hover:text-white"}`}>
          <FlipHorizontal size={20} />
        </button>
        <span className="text-[10px] text-white/40 hidden sm:block">Space = play/pause · Esc = exit</span>
        <button data-testid="teleprompter-close-btn" onClick={() => navigate(-1)} className="text-white/60 hover:text-[#FF4D4D] transition-colors">
          <X size={22} weight="bold" />
        </button>
      </div>
    </div>
  );
}
